import { to } from "react-lib/utils";
import BaseService from "react-lib/apis/services/BaseService";
import CONFIG from "config/config";

import { RedmineTokenResponse, GetRedmineToken } from "../sequence/ProjectList";

/*================================ TYPES ==============================*/

enum METHOD {
  GET = "get",
  POST = "post",
  PUT = "put",
  PATCH = "patch",
  DELETE = "delete",
}

type CallOptions = {
  method: METHOD;
  url: string;
  params?: Record<string, any>;
  data?: any; // for POST/PUT/PATCH
};

export type CreateProjectPayloadType = Partial<{
  project: CreateProjectType;
}>;

export type CreateIssuePayloadType = Partial<{
  issue: CreateIssueType;
}>;

export type CreateProjectType = Partial<{
  name: string;
  identifier: string;
  description: string;
  is_public: boolean;
  tracker_ids: number[];
  custom_field_values: Record<number | string, any[]>;
  custom_fields?: Partial<{
    id: number;
    name: string;
    multiple: boolean;
    value: string[] | string;
  }>[];
}>;

export type CreateIssueType = Partial<{
  // edit
  id?: number;
  notes?: string;
  // create
  project_id: number;
  tracker_id: number;
  status_id: number;
  priority_id: number;
  subject: string;
  description: string;
  start_date: string;
  due_date: string;
  assigned_to_id: number;
  custom_fields: Partial<{
    id: number;
    value: string;
    name: string;
  }>[];
}>;

/*================================ CONSTANTS ==============================*/

const HOST = CONFIG.REDMINE;

const REDMINE_URL = {
  projects: {
    list: `/projects.json`,
    detail: (id: number) => `/projects/${id}.json`,
    name: (name: string) => `/projects.json?name=${name}`,
    archive: (id: number, action: "archive" | "unarchive") => `/projects/${id}/${action}.json`,
  },
  issues: {
    list: `/issues.json`,
    detail: (id: number) => `/issues/${id}.json`,
    issues_tracking: (id: number) => `/issues.json?project_id=${id}`, // issues tracking (ใช้ใน Project Menu)
  },
  memberships: {
    list: (id: number) => `/projects/${id}/memberships.json`,
  },
  statuses: {
    list: `/issue_statuses.json`,
  },
  trackers: {
    list: `/trackers.json`,
  },
  enumerations: {
    priority: `/enumerations/issue_priorities.json`,
    activities: `/enumerations/time_entry_activities.json`,
    categories: `/enumerations/document_categories.json`,
  },
};

/*================================ BASE ==============================*/

const callRedmine = async (controller: any, options: CallOptions) => {
  const { method = "get", url, params, data } = options;
  const base = await createAuth(controller);

  const [err, resp, raw] = await to(
    base.client[method](
      `${HOST}${url}`,
      ["get", "delete"].includes(method) ? { params } : data,
      ["get", "delete"].includes(method) ? undefined : { params }
    )
      .then(base.handleResponse)
      .catch(base.throwErrorMessage)
  );

  if (err || (!resp && ![204].includes(raw?.status))) {
    const status = raw?.response?.status || raw?.status;
    const dirErr = err.errors;
    const message = raw?.message || "Unknown Redmine Error";

    if (status) {
      throw { errors: dirErr ? dirErr : [`Redmine Error [${status}]: ${message}`] };
    }

    throw { errors: [`Redmine Unknown Error: ${message}`] };
  }

  return resp;
};

/*================================ AUTHEN ==============================*/

let cachedRedmineUser: {
  token: string;
  header: string;
  user: string;
} | null = null;

const createAuth = async (controller: any): Promise<BaseService> => {
  const currentUser = controller.apiToken;

  if (cachedRedmineUser && cachedRedmineUser.user === currentUser) {
    const base = new BaseService();
    base.client.defaults.headers.common[cachedRedmineUser.header] = cachedRedmineUser.token;

    return base;
  }

  const [tokenResp, tokenErr]: [RedmineTokenResponse | null, any] = await GetRedmineToken(
    controller,
    {}
  );

  if (tokenErr || !tokenResp) throw tokenErr?.detail ?? tokenErr;

  const { token, header } = tokenResp;

  cachedRedmineUser = {
    token,
    header,
    user: currentUser,
  };

  const base = new BaseService();
  base.client.defaults.headers.common[header] = token;

  return base;
};

/*================================ PROJECTS ==============================*/

export const getProjectList = async ({
  controller,
  params = {},
}: {
  controller: any;
  params?: Record<string, any>;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.projects.list,
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const getProjectDetail = async ({
  controller,
  params = {},
  pk,
}: {
  controller: any;
  params?: Record<string, any>;
  pk: number;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.projects.detail(pk),
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const getProjectName = async ({
  controller,
  params = {},
  name,
}: {
  controller: any;
  params?: Record<string, any>;
  name: string;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.projects.name(name),
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const createProject = async ({
  controller,
  data = {},
}: {
  controller: any;
  data: CreateProjectPayloadType;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.POST,
      url: REDMINE_URL.projects.list,
      data,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const editProject = async ({
  controller,
  data = {},
  pk,
}: {
  controller: any;
  data: CreateProjectPayloadType;
  pk: number;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.PUT,
      url: REDMINE_URL.projects.detail(pk),
      data,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const archiveProject = async ({
  controller,
  pk,
  action,
}: {
  controller: any;
  pk: number;
  action: "archive" | "unarchive";
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.PUT,
      url: REDMINE_URL.projects.archive(pk, action),
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

/*================================ ISSUES ==============================*/

export const getIssuesList = async ({
  controller,
  params = {},
}: {
  controller: any;
  params?: Record<string, any>;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.issues.list,
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const getIssuesDetail = async ({
  controller,
  params = {},
  pk,
}: {
  controller: any;
  params?: Record<string, any>;
  pk: number;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.issues.detail(pk),
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const getAllIssuesTracking = async ({
  controller,
  params = {},
  pk,
}: {
  controller: any;
  params?: Record<string, any>;
  pk: number;
}) => {
  try {
    let totalCount = 0;
    let offset = 0;
    const limit = 100;
    const allIssues: any[] = [];

    while (true) {
      const currentParams = {
        ...params,
        offset,
        limit,
        status_id: "*",
      };

      const result = await callRedmine(controller, {
        method: METHOD.GET,
        url: REDMINE_URL.issues.issues_tracking(pk),
        params: currentParams,
      });

      if (offset === 0) {
        totalCount = result.total_count;
      }

      allIssues.push(...(result.issues || []));

      offset += result.issues.length;

      if (offset >= totalCount) break;
    }

    return [allIssues, null];
  } catch (error) {
    return [null, error];
  }
};

export const createIssue = async ({
  controller,
  data = {},
}: {
  controller: any;
  data: CreateIssuePayloadType;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.POST,
      url: REDMINE_URL.issues.list,
      data,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

export const editIssue = async ({
  controller,
  data = {},
  pk,
}: {
  controller: any;
  data: CreateIssuePayloadType;
  pk: number;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.PUT,
      url: REDMINE_URL.issues.detail(pk),
      data,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

/*================================ MEMBERSHIPS ==============================*/

export const getMembershipsList = async ({
  controller,
  params = {},
  pk,
}: {
  controller: any;
  params?: Record<string, any>;
  pk: number;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.memberships.list(pk),
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

/*================================ STATUSES ==============================*/

export const getIssuesStatusList = async ({
  controller,
  params = {},
}: {
  controller: any;
  params?: Record<string, any>;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.statuses.list,
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

/*================================ TRACKERS ==============================*/

export const getTrackerList = async ({
  controller,
  params = {},
}: {
  controller: any;
  params?: Record<string, any>;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.trackers.list,
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

/*================================ ENUMERATIONS ==============================*/

export const getPriorityList = async ({
  controller,
  params = {},
}: {
  controller: any;
  params?: Record<string, any>;
}) => {
  try {
    const result = await callRedmine(controller, {
      method: METHOD.GET,
      url: REDMINE_URL.enumerations.priority,
      params,
    });

    return [result, null];
  } catch (error) {
    return [null, error];
  }
};
